import { useState, useMemo, useRef } from 'react'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import './PropertiesPanel.css'

const COLORS = ['#E85D04', '#06D6A0', '#118AB2', '#9B5DE5', '#EF476F', '#FFD166', '#1D3557', '#2A9D8F']

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed': return '✓'
    case 'active': return '●'
    default: return '○'
  }
}

const formatDuration = (minutes) => {
  if (!minutes) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0 && mins > 0) return `${hours}t ${mins}m`
  if (hours > 0) return `${hours}t`
  return `${mins}m`
}

export default function PropertiesPanel() {
  const {
    project,
    selectedFlow,
    selectedCheckpoint,
    setSelectedFlow,
    setSelectedCheckpoint,
    updateFlow,
    deleteFlow,
    updateCheckpoint,
    deleteCheckpoint,
    addCheckpoint,
    addSubtask,
    toggleSubtask,
    updateSubtask,
    deleteSubtask,
    reorderSubtasks,
    reorderCheckpoints,
    projectRole
  } = useProject()
  const { t, language } = useI18n()
  const canEdit = projectRole !== 'VIEWER'
  const [newSubtaskText, setNewSubtaskText] = useState('')
  const [draggedSubtaskIndex, setDraggedSubtaskIndex] = useState(null)
  const [draggedCheckpointIndex, setDraggedCheckpointIndex] = useState(null)
  const [editingSubtaskIndex, setEditingSubtaskIndex] = useState(null)
  const [editingSubtaskText, setEditingSubtaskText] = useState('')
  const dragOverIndexRef = useRef(null)
  const checkpointDragOverIndexRef = useRef(null)

  const isNorwegian = language === 'nb'

  // Get selected flow and checkpoint data
  const flow = project.flows.find(f => f.id === selectedFlow)
  const checkpoint = flow?.checkpoints.find(c => c.id === selectedCheckpoint)

  // Calculate cumulative times
  const cumulativeTimes = useMemo(() => {
    if (!flow) return []
    let cumulative = 0
    return flow.checkpoints.map(cp => {
      cumulative += (cp.estimatedDuration || 0)
      return cumulative
    })
  }, [flow])

  const totalDuration = cumulativeTimes.length > 0 ? cumulativeTimes[cumulativeTimes.length - 1] : 0

  const handleClose = () => {
    setSelectedFlow(null)
    setSelectedCheckpoint(null)
  }

  const handleAddCheckpoint = () => {
    if (!selectedFlow) return
    const checkpointNumber = flow ? flow.checkpoints.length + 1 : 1
    const lastCp = flow?.checkpoints[flow.checkpoints.length - 1]
    const newCheckpoint = addCheckpoint(
      selectedFlow,
      `${t('checkpoint.typeCheckpoint')} ${checkpointNumber}`,
      lastCp ? lastCp.time + 1 : 0,
      lastCp ? lastCp.distance : 0
    )
    setSelectedCheckpoint(newCheckpoint.id)
  }

  const handleStatusCycle = (e, cpId) => {
    e.stopPropagation()
    const cp = flow.checkpoints.find(c => c.id === cpId)
    if (!cp) return
    const statusOrder = ['pending', 'active', 'completed']
    const currentIndex = statusOrder.indexOf(cp.status)
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length]
    updateCheckpoint(flow.id, cpId, { status: nextStatus })
  }

  const handleDurationChange = (field, value) => {
    if (!checkpoint) return
    const currentHours = Math.floor((checkpoint.estimatedDuration || 0) / 60)
    const currentMinutes = (checkpoint.estimatedDuration || 0) % 60

    let newHours = currentHours
    let newMinutes = currentMinutes

    if (field === 'hours') {
      newHours = parseInt(value) || 0
    } else {
      newMinutes = parseInt(value) || 0
    }

    const newDuration = (newHours * 60) + newMinutes
    updateCheckpoint(flow.id, checkpoint.id, { estimatedDuration: newDuration })
  }

  const handleAddSubtask = (e) => {
    e.preventDefault()
    if (newSubtaskText.trim() && selectedFlow && selectedCheckpoint) {
      addSubtask(selectedFlow, selectedCheckpoint, newSubtaskText.trim())
      setNewSubtaskText('')
    }
  }

  // Drag and drop handlers for subtasks
  const handleSubtaskDragStart = (e, index) => {
    setDraggedSubtaskIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.currentTarget)
  }

  const handleSubtaskDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    dragOverIndexRef.current = index
  }

  const handleSubtaskDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedSubtaskIndex !== null && draggedSubtaskIndex !== dropIndex) {
      reorderSubtasks(selectedFlow, selectedCheckpoint, draggedSubtaskIndex, dropIndex)
    }
    setDraggedSubtaskIndex(null)
    dragOverIndexRef.current = null
  }

  const handleSubtaskDragEnd = () => {
    setDraggedSubtaskIndex(null)
    dragOverIndexRef.current = null
  }

  // Drag and drop handlers for checkpoints
  const handleCheckpointDragStart = (e, index) => {
    setDraggedCheckpointIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.currentTarget)
  }

  const handleCheckpointDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    checkpointDragOverIndexRef.current = index
  }

  const handleCheckpointDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedCheckpointIndex !== null && draggedCheckpointIndex !== dropIndex) {
      reorderCheckpoints(selectedFlow, draggedCheckpointIndex, dropIndex)
    }
    setDraggedCheckpointIndex(null)
    checkpointDragOverIndexRef.current = null
  }

  const handleCheckpointDragEnd = () => {
    setDraggedCheckpointIndex(null)
    checkpointDragOverIndexRef.current = null
  }

  const handleEditSubtask = (index, currentText) => {
    setEditingSubtaskIndex(index)
    setEditingSubtaskText(currentText)
  }

  const handleSaveSubtask = (index) => {
    if (editingSubtaskText.trim() && selectedFlow && selectedCheckpoint) {
      updateSubtask(selectedFlow, selectedCheckpoint, index, editingSubtaskText.trim())
    }
    setEditingSubtaskIndex(null)
    setEditingSubtaskText('')
  }

  const handleCancelEdit = () => {
    setEditingSubtaskIndex(null)
    setEditingSubtaskText('')
  }

  if (!flow) {
    return null
  }

  const distanceLabels = project.distanceLabels?.length > 0
    ? project.distanceLabels
    : ['Start', 'Phase 1', 'Phase 2', 'Phase 3', 'Complete']

  return (
    <aside className="properties-panel">
      <div className="panel-header">
        <h2>{flow.name}</h2>
        <button className="close-btn" onClick={handleClose}>×</button>
      </div>

      <div className="panel-content">
        {/* Flow name and color */}
        <div className="flow-header-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('flow.name')}</label>
            <input
              type="text"
              value={flow.name}
              onChange={(e) => updateFlow(flow.id, { name: e.target.value })}
              disabled={!canEdit}
              readOnly={!canEdit}
            />
          </div>
          <div className="color-swatches">
            {COLORS.slice(0, 4).map(color => (
              <div
                key={color}
                className={`color-swatch ${flow.color === color ? 'selected' : ''} ${!canEdit ? 'disabled' : ''}`}
                style={{ backgroundColor: color, ...(canEdit ? {} : { pointerEvents: 'none', opacity: 0.5 }) }}
                onClick={() => canEdit && updateFlow(flow.id, { color })}
              />
            ))}
          </div>
        </div>

        {/* Tasks section */}
        <div className="tasks-section">
          <div className="tasks-header">
            <label>{isNorwegian ? 'Oppgaver' : 'Tasks'} ({flow.checkpoints.length})</label>
            <button className="btn-add-task" onClick={handleAddCheckpoint} disabled={!canEdit}>
              + {isNorwegian ? 'Legg til' : 'Add'}
            </button>
          </div>

          <div className="checkpoint-list">
            {flow.checkpoints.length === 0 ? (
              <div className="empty-tasks">
                {isNorwegian ? 'Ingen oppgaver enda' : 'No tasks yet'}
              </div>
            ) : (
              flow.checkpoints.map((cp, index) => {
                const isSelected = selectedCheckpoint === cp.id
                const hasDuration = cp.estimatedDuration > 0

                return (
                  <div
                    key={cp.id}
                    className={`checkpoint-list-item ${isSelected ? 'selected' : ''} ${draggedCheckpointIndex === index ? 'dragging' : ''}`}
                    onClick={() => setSelectedCheckpoint(cp.id)}
                    draggable={canEdit}
                    onDragStart={canEdit ? (e) => handleCheckpointDragStart(e, index) : undefined}
                    onDragOver={canEdit ? (e) => handleCheckpointDragOver(e, index) : undefined}
                    onDrop={canEdit ? (e) => handleCheckpointDrop(e, index) : undefined}
                    onDragEnd={canEdit ? handleCheckpointDragEnd : undefined}
                  >
                    <span className="drag-handle">☰</span>
                    <span className="cp-number">{index + 1}.</span>
                    <span
                      className={`cp-status-icon status-${cp.status}`}
                      onClick={canEdit ? (e) => handleStatusCycle(e, cp.id) : (e) => e.stopPropagation()}
                      title={t(`checkpoint.${cp.status}`)}
                    >
                      {getStatusIcon(cp.status)}
                    </span>
                    <span className="cp-name">{cp.name}</span>
                    {hasDuration && (
                      <div className="cp-duration-info">
                        <span className="cp-duration">{cp.estimatedDuration}m</span>
                        <span className="cp-cumulative" title={isNorwegian ? 'Kumulativ tid' : 'Cumulative time'}>
                          Σ {formatDuration(cumulativeTimes[index])}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {totalDuration > 0 && (
            <div className="total-duration">
              <span>{isNorwegian ? 'Total estimert tid' : 'Total estimated time'}:</span>
              <span className="total-value">{formatDuration(totalDuration)}</span>
            </div>
          )}
        </div>

        {/* Selected checkpoint editor */}
        {checkpoint && (
          <div className="checkpoint-editor">
            <div className="editor-header">
              <span>{isNorwegian ? 'Rediger oppgave' : 'Edit task'}</span>
              <button onClick={() => setSelectedCheckpoint(null)}>×</button>
            </div>

            <div className="form-group">
              <label>{isNorwegian ? 'Navn' : 'Name'}</label>
              <input
                type="text"
                value={checkpoint.name}
                onChange={(e) => updateCheckpoint(flow.id, checkpoint.id, { name: e.target.value })}
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{isNorwegian ? 'Timer' : 'Hours'}</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={Math.floor((checkpoint.estimatedDuration || 0) / 60) || ''}
                  onChange={(e) => handleDurationChange('hours', e.target.value)}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
              </div>
              <div className="form-group">
                <label>{isNorwegian ? 'Minutter' : 'Minutes'}</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  step="5"
                  placeholder="0"
                  value={(checkpoint.estimatedDuration || 0) % 60 || ''}
                  onChange={(e) => handleDurationChange('minutes', e.target.value)}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('checkpoint.status')}</label>
                <select
                  value={checkpoint.status}
                  onChange={(e) => updateCheckpoint(flow.id, checkpoint.id, { status: e.target.value })}
                  disabled={!canEdit}
                >
                  <option value="pending">{t('checkpoint.pending')}</option>
                  <option value="active">{t('checkpoint.active')}</option>
                  <option value="completed">{t('checkpoint.completed')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('checkpoint.type')}</label>
                <select
                  value={checkpoint.type}
                  onChange={(e) => updateCheckpoint(flow.id, checkpoint.id, { type: e.target.value })}
                  disabled={!canEdit}
                >
                  <option value="checkpoint">{t('checkpoint.typeCheckpoint')}</option>
                  <option value="milestone">{t('checkpoint.typeMilestone')}</option>
                  <option value="gate">{t('checkpoint.typeGate')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('checkpoint.time')}</label>
                <input
                  type="number"
                  min="0"
                  max="52"
                  step="0.5"
                  value={checkpoint.time}
                  onChange={(e) => updateCheckpoint(flow.id, checkpoint.id, { time: parseFloat(e.target.value) })}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
              </div>
              <div className="form-group">
                <label>{t('checkpoint.distance')}</label>
                <select
                  value={checkpoint.distance}
                  onChange={(e) => updateCheckpoint(flow.id, checkpoint.id, { distance: parseInt(e.target.value) })}
                  disabled={!canEdit}
                >
                  {distanceLabels.map((label, i) => (
                    <option key={i} value={i}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subtasks / Checklist */}
            <div className="subtasks-section">
              <div className="subtasks-header">
                <label>{isNorwegian ? 'Huskeliste' : 'Checklist'}</label>
                <span className="subtasks-count">
                  {(checkpoint.subtasks || []).filter(s => s.done).length}/{(checkpoint.subtasks || []).length}
                </span>
              </div>

              <div className="subtasks-list">
                {(checkpoint.subtasks || []).map((subtask, index) => (
                  <div
                    key={index}
                    className={`subtask-item ${draggedSubtaskIndex === index ? 'dragging' : ''}`}
                    draggable={canEdit}
                    onDragStart={canEdit ? (e) => handleSubtaskDragStart(e, index) : undefined}
                    onDragOver={canEdit ? (e) => handleSubtaskDragOver(e, index) : undefined}
                    onDrop={canEdit ? (e) => handleSubtaskDrop(e, index) : undefined}
                    onDragEnd={canEdit ? handleSubtaskDragEnd : undefined}
                  >
                    <span className="subtask-drag-handle">☰</span>
                    <input
                      type="checkbox"
                      checked={subtask.done}
                      onChange={() => toggleSubtask(flow.id, checkpoint.id, index)}
                      disabled={!canEdit}
                    />
                    {editingSubtaskIndex === index ? (
                      <input
                        type="text"
                        className="subtask-edit-input"
                        value={editingSubtaskText}
                        onChange={(e) => setEditingSubtaskText(e.target.value)}
                        onBlur={() => handleSaveSubtask(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveSubtask(index)
                          } else if (e.key === 'Escape') {
                            handleCancelEdit()
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className={`subtask-text ${subtask.done ? 'done' : ''}`}
                        onClick={canEdit ? () => handleEditSubtask(index, subtask.text) : undefined}
                      >
                        {subtask.text}
                      </span>
                    )}
                    <button
                      className="subtask-delete"
                      onClick={() => deleteSubtask(flow.id, checkpoint.id, index)}
                      disabled={!canEdit}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddSubtask} className="add-subtask-form">
                <input
                  type="text"
                  value={newSubtaskText}
                  onChange={(e) => setNewSubtaskText(e.target.value)}
                  placeholder={isNorwegian ? 'Legg til element...' : 'Add item...'}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <button type="submit" disabled={!canEdit}>+</button>
              </form>
            </div>

            <button
              className="btn btn-danger"
              onClick={() => deleteCheckpoint(flow.id, checkpoint.id)}
              disabled={!canEdit}
            >
              {t('checkpoint.delete')}
            </button>
          </div>
        )}

        {/* Delete flow button (only when no checkpoint selected) */}
        {!checkpoint && (
          <button
            className="btn btn-danger"
            onClick={() => deleteFlow(flow.id)}
          >
            {t('flow.delete')}
          </button>
        )}
      </div>
    </aside>
  )
}
